import type { ResolvedWidgetValue, WidgetTone } from "../../types";

export const TONE_LABEL: Record<WidgetTone, string> = {
  ok: "OK",
  warn: "ALERTA",
  danger: "PERIGO",
  info: "INFO",
  neutral: "NEUTRO",
};

/**
 * Tom resolvido para o badge do rodapé. Quando o snapshot estiver
 * indisponível, sempre cai em "muted" (cinza) para evitar verde verde
 * estampado em widget sem sinal.
 */
export function getFooterStatus(value: ResolvedWidgetValue): "ok" | "warn" | "danger" | "muted" {
  if (!value.isAvailable) return "muted";
  if (value.tone === "warn") return "warn";
  if (value.tone === "danger") return "danger";
  return "ok";
}

export function getToneLabel(value: ResolvedWidgetValue): string {
  if (!value.isAvailable) return "SEM SINAL";
  return TONE_LABEL[value.tone];
}

export function formatWidgetNumber(value: number | null, decimals = 1): string {
  // `!Number.isFinite` cobre null, NaN E ±Infinity num só guard — nunca
  // exibir "NaN"/"Infinity"/"-Infinity" para o operador (P0.4).
  if (value === null || !Number.isFinite(value)) {
    return "--";
  }

  return value.toLocaleString("pt-BR", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Texto relativo (ex.: "há 3s", "há 12min") para o rodapé do widget. Usa o
 * timestamp do snapshot — quando ausente, retorna `null` e o caller mostra
 * "—". Diferente da versão anterior, que mostrava "--:--:--" sempre.
 */
export function formatWidgetRelative(iso: string | null, now: Date = new Date()): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;

  const diffMs = now.getTime() - date.getTime();
  const sec = Math.max(0, Math.round(diffMs / 1000));
  if (sec < 60) return `há ${sec}s`;
  const min = Math.round(sec / 60);
  if (min < 60) return `há ${min}min`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `há ${hr}h`;
  const day = Math.round(hr / 24);
  return `há ${day}d`;
}

/**
 * Data/hora absoluta da medição (pt-BR) para o tooltip de proveniência
 * (P0.3). O operador passa o mouse no "há Xs" e vê o instante exato em que
 * o hardware mediu — base da confiança no dado.
 */
export function formatWidgetAbsolute(iso: string | null): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

/**
 * Texto de proveniência para o tooltip do rodapé: quando o dado foi medido
 * + de onde veio (tag do sinal). Fonte da verdade rastreável por widget.
 */
export function buildProvenanceTitle(
  measuredAt: string | null | undefined,
  source: string | undefined,
): string {
  const absolute = formatWidgetAbsolute(measuredAt ?? null);
  const origin = source ? ` · ${source}` : "";
  return absolute ? `Medido em ${absolute}${origin}` : `Sem leitura registrada${origin}`;
}

export function getClampedPercent(value: number | null, min = 0, max = 100): number {
  if (value === null || max <= min) {
    return 0;
  }

  return Math.max(0, Math.min(100, ((value - min) / (max - min)) * 100));
}
